import { useMemo } from 'react';

import useSWR from 'swr';

import type { DisplayUser } from '@/lib/api';
import { urlFetcher } from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { followsMap } from '@/lib/follows';

/** Defines the inputs for shared follow-suggestion filtering. */
export interface UseFollowSuggestionsParams {
  /** Disables fetching when the authenticated user is unavailable. */
  userInfo: DisplayUser | null;
  /** Excludes additional usernames such as locally ignored suggestions. */
  excludedUsernames?: Set<string>;
}

/**
 * Returns filtered follow suggestions for authenticated discovery surfaces.
 *
 * Reuses the existing `/feed/follows` contract while removing already-followed
 * users and any caller-specified exclusions.
 */
export function useFollowSuggestions({ userInfo, excludedUsernames }: UseFollowSuggestionsParams): {
  suggestions: DisplayUser[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, error, isLoading } = useSWR<DisplayUser[]>(
    userInfo ? `${API_URL}/feed/follows` : null,
    urlFetcher
  );

  const followedUsernames = useMemo(() => {
    if (!userInfo) {
      return new Map();
    }

    return followsMap(userInfo.followed_users);
  }, [userInfo]);

  const suggestions = useMemo(() => {
    if (!userInfo) {
      return [];
    }

    const excluded = excludedUsernames ?? new Set<string>();
    return (
      data?.filter(
        (suggestedUser) =>
          !followedUsernames.has(suggestedUser.username) && !excluded.has(suggestedUser.username)
      ) ?? []
    );
  }, [data, excludedUsernames, followedUsernames, userInfo]);

  return { suggestions, isLoading, error };
}
